import { defineStore } from 'pinia'
import { convertArrayToObject } from 'src/utils'
import { useAuthStore } from 'src/stores/auth'
import { usePersistedStore } from 'src/stores/persisted'

export const useFavoriteStore = defineStore('favorites', {
  state: () => ({
    favorites: {},
    loading: true,
    loadingCategory: false,
    totalCount: 0,
    markedAsFavorites: {},
    sortOptions: [
      { label: 'Category', value: 0 },
      { label: 'Most Frequently Purchased', value: 1 },
      { label: 'Recently Purchased', value: 2 },
      { label: 'Price High to Low', value: 3 },
      { label: 'Price Low to High', value: 4 },
    ],
    defaultParams: { sortBy: 0, PageSize: 20, Page: 1, category: '' },
    categories: [
      {
        categoryID: '',
        categoryName: 'All',
      },
    ],
  }),
  getters: {
    isProductMarkedAsFavorite(state) {
      return (productId, isFavorite) => {
        if (state.markedAsFavorites[productId]) {
          return state.markedAsFavorites[productId].isFavorite
        }

        return isFavorite
      }
    },
    favoriteItemsCount(state) {
      let count = 0

      for (let key in state.favorites) {
        let item = state.favorites[key]

        if (this.isProductMarkedAsFavorite(item.productID, item.isFavorite)) {
          count++
        }
      }

      return count
    },
    nextPageNumber() {
      return (currentPage, perPage) => {
        let maxPages = Math.ceil(this.totalCount / perPage) || 1
        return currentPage < maxPages ? currentPage : maxPages
      }
    },
  },
  actions: {
    async toggleProductIsFavorite(products = []) {
      let { data } = await this.$axiosInstance.post('/Favorite', {
        favoriteProducts: products,
      })

      if (data.data) {
        const favorites = convertArrayToObject(data.data.products, 'productID')
        this.markedAsFavorites = Object.assign({}, this.markedAsFavorites, {
          ...favorites,
        })
      }

      return data
    },
    /**
     * @param {object} params Request query params (includes `Page`, etc.)
     * @param {{ append?: boolean }} [options]
     * @param {boolean} [options.append] If true and `params.Page` > 1, merge into `favorites` (infinite scroll).
     *        If false/omitted, each page replaces `favorites` (pagination controls).
     */
    async getFavorites(params, { append = false } = {}) {
      const authStore = useAuthStore()
      const persistedStore = usePersistedStore()

      if (!persistedStore.currentLocationId || !authStore.customerExists)
        return false

      try {
        if (params.Page == 1) this.loading = true
        let { data } = await this.$axiosInstance.get('/Favorite', {
          params: {
            locationID: persistedStore.currentLocationId,
            fulfillmentCodeName: persistedStore.selectedFulfillmentCodeName,
            zipCode: persistedStore.locationOrShippingZip,
            ...params,
          },
        })

        this.totalCount = data.data.count
        let favorites = convertArrayToObject(data.data.items, 'productID')

        if (params.Page == 1) {
          this.favorites = Object.assign({}, favorites)
          this.loading = false
        } else if (append) {
          this.favorites = Object.assign({}, this.favorites, favorites)
        } else {
          this.favorites = Object.assign({}, favorites)
        }

        return data.data
      } catch (error) {}
    },
    async removeItemFromFavorites({ productId, objKey }) {
      let params = [
        {
          productID: productId,
          markAsFavorite: false,
        },
      ]

      this.toggleProductIsFavorite(params)
      delete this.favorites[objKey]
      this.totalCount -= 1
    },
    async getCategories(params) {
      const authStore = useAuthStore()
      const persistedStore = usePersistedStore()

      if (!persistedStore.currentLocationId || !authStore.isLoggedIn)
        return false

      try {
        this.loadingCategory = true
        this.$axiosInstance
          .get('/Favorite/Category', {
            params: {
              locationID: persistedStore.currentLocationId,
              fulfillmentCodeName: persistedStore.selectedFulfillmentCodeName,
              zipCode: persistedStore.locationOrShippingZip,
              ...params,
            },
          })
          .then((response) => {
            this.categories = [...this.categories, ...response.data.data]
          })
          .finally(() => {
            this.loadingCategory = false
          })
      } catch (error) {}
    },
    setFavorites(favorites) {
      this.favorites = Object.assign({}, favorites)
    },
    resetState() {
      this.$reset()
    },
  },
})
